import { Button, Typography } from '@mui/material';
import { Execution, OrderState, OrderSymbol } from '@protos/trading';
import { Order, Side } from '@protos/v2/order';
import { priceFormatterService } from '@services/PriceFormatterService';
import darkTheme from '@shared/themes/darkTheme';
import { CustomToolPanelProps } from 'ag-grid-react';
import { clamp } from 'lodash';
import { CSSProperties } from 'react';

export interface OrderDetailsPanelProps {
  order: Order | null;
  cancelOrder: (orderId: string) => void;
}

type OrderDetailsPanelPropsForGrid = CustomToolPanelProps & OrderDetailsPanelProps;

const panelStyle: CSSProperties = {
  padding: '1em .5em',
  width: '196px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyItems: 'flex-start',
  gap: '4px',
};

function symbolToString(symbol: OrderSymbol): string {
  if (typeof symbol === 'string') return symbol;
  if (typeof symbol === 'object') return symbol.front + ' / ' + symbol.back;
  return '';
}

interface PanelRowPropsForType<T> {
  itemKey: keyof T;
  displayName: string;
  item: T;
  formatter?: (itemKey: keyof T, item: T) => string;
  index: number;
}

function PanelRow<T>(props: PanelRowPropsForType<T>): JSX.Element {
  const { itemKey, displayName, item, formatter, index } = props;

  const key = 'row' + (itemKey as string) + index;

  return (
    <div
      style={{ display: 'flex', flexDirection: 'row', marginBottom: '2px', gap: '5%' }}
      key={key}
      id={key}
      title={`${displayName} = ${String(item[itemKey]).toUpperCase()}`}
    >
      <div style={{ width: '30%', textAlign: 'right' }}>{displayName}:</div>
      <div
        style={{
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          maxWidth: '65%',
          textTransform: 'uppercase',
        }}
      >
        {formatter ? formatter(itemKey, item) : String(item[itemKey])}
      </div>
    </div>
  );
}

const HorizontalBreak = (props: { size?: number }) => {
  return <hr style={{ width: '100%', borderColor: '#222222', borderWidth: `${props.size || 1}px` }} />;
};

const orderKeys = ['id', 'order_type', 'exchange', 'amount', 'price', 'order_state'];
const orderDisplayNames = ['ID', 'Type', 'Exchange', 'Amount', 'Price', 'State'];
const executionKeys = ['symbol', 'price', 'amount', 'local_received_timestamp'] as (keyof Execution)[];
const executionDisplayNames = ['Symbol', 'Price', 'Amount', 'Time'];
const executionFormatters = [
  undefined,
  (itemKey: keyof Execution, item: Execution) => {
    return priceFormatterService.formatProductPrice(item.price);
  },
  undefined,
  (itemKey: keyof Execution, item: Execution) => {
    return new Date(item.local_received_timestamp).toISOString();
  },
];

const ExecutionSection = (props: OrderDetailsPanelProps) => {
  const { order } = props;
  if (!order) return <></>;

  const { executions } = order;
  if (executions.length === 0) {
    return (
      <div style={{ flexShrink: 1 }}>
        <div>No executions</div>
      </div>
    );
  }

  return (
    <div style={{ flexShrink: 1, overflow: 'scroll', flexGrow: 1 }}>
      {executions.map((e, i) => {
        return (
          <div key={e.id || i}>
            {executionKeys
              .map((k, i) => {
                return {
                  itemKey: k as keyof Execution,
                  displayName: executionDisplayNames[i],
                  formatter: executionFormatters[i],
                  item: e,
                  index: i,
                };
              })
              .map(r => {
                return <PanelRow<Execution> {...r} key={r.index} />;
              })}
            <HorizontalBreak size={1} />
          </div>
        );
      })}
    </div>
  );
};

const LimitOrderSection = (props: OrderDetailsPanelProps & { colour: string }) => {
  const { order, cancelOrder } = props;

  if (order?.order_type !== 'limit') {
    return <></>;
  }

  const amount = Number(order.amount);
  const executed_amount = clamp(Number(order.executed_amount), 0, amount);

  let fillRatio = 0;

  if (!isNaN(amount) && !isNaN(executed_amount)) {
    fillRatio = Number(executed_amount) / Number(amount);
  }

  const isFilled = fillRatio === 1.0;
  const isCancellable = [OrderState.PENDING_CREATED, OrderState.CREATED, OrderState.NEW, OrderState.PARTIALLY_FILLED].includes(order.order_state);

  return (
    <>
      <HorizontalBreak />
      <Typography variant="h6" style={{ marginBottom: '.3rem', textAlign: 'left' }}>
        Limit Details
      </Typography>
      <div style={{ position: 'relative', width: '100%', height: '16px', backgroundColor: 'white' }}>
        <div
          style={{
            top: 0,
            left: 0,
            width: 100 * fillRatio + '%',
            height: '16px',
            backgroundColor: props.colour,
            minWidth: '1px',
            transition: 'all 0.1s ease',
          }}
          title={`${100 * fillRatio}% filled`}
        ></div>
      </div>
      <div style={{ paddingTop: '4px', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <div>Total: {order.amount}</div>
        <div>Filled: {order.executed_amount || 0}</div>
        <div>Remaining: {Number(order.amount) - Number(order.executed_amount)}</div>
      </div>

      <Button variant={'contained'} onClick={() => cancelOrder(order.id)} disabled={isFilled || !isCancellable} sx={{ transition: 'all 0.1s ease' }}>
        Cancel Order
      </Button>
    </>
  );
};

export const OrderDetailsPanel = (props: OrderDetailsPanelPropsForGrid) => {
  const { order } = props;

  if (order === null) {
    return (
      <div style={panelStyle}>
        <Typography variant={'h6'}>No order selected</Typography>
      </div>
    );
  }

  const { side, symbol } = order;

  const sideColour = side === Side.BUY ? darkTheme.palette.background.buyHover : darkTheme.palette.background.sellHover;

  const title = `${symbolToString(symbol)}`;
  return (
    <div style={panelStyle}>
      <Typography
        variant={'h6'}
        sx={{ textAlign: 'left', textTransform: 'uppercase', backgroundColor: sideColour, padding: '1px 0 2px .4rem', marginBottom: '.4rem' }}
      >
        <span>{side}&nbsp;</span>
        <span>{title}</span>
      </Typography>

      <div style={{ display: 'flex', gap: '0px', flexDirection: 'column' }}>
        {orderKeys
          .map((k, i) => {
            return { itemKey: k as keyof Order, displayName: orderDisplayNames[i], item: order };
          })
          .map((info, index) => (
            <PanelRow {...info} index={index} key={info.itemKey + index} />
          ))}
      </div>
      <LimitOrderSection {...props} colour={sideColour} />
      <Typography variant="h6" style={{ margin: '.5rem 0 .3rem', textAlign: 'left' }}>
        Executions
      </Typography>
      <ExecutionSection {...props} />
    </div>
  );
};
